import { Button, Group, Tabs } from '@mantine/core';
import { IconChevronRight, IconCopy, IconDeviceFloppy, IconTrash } from '@tabler/icons-react';
import queryString from 'query-string';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { CHROME_CSS_STYLE_KEYS } from '../../constants/bannerChrome';
import getMergeTags from '../../constants/getMergeTags';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import LockableButton from '../Lockable/LockableButton';
import ConfirmationModal from '../Modals/ConfirmationModal';
import AudienceBannerForm from './audienceBannerForm';
import ContentBannerForm from './contentBannerForm';

const tabs = ['content', 'audience'];

const BannerForm = ({ banner: passedBanner, deleteBanner, saveBanner, cancel, onSuccess, copyBanner }) => {
  const fetch = useFetch();
  const { accountId, domainId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const search = queryString.parse(location.search);
  const bannerTab = search.bannerTab || tabs[0];
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [assets, setAssets] = useState();
  const [segments, setSegments] = useState();
  const [banner, setBanner] = useState(passedBanner);
  const [userError, setUserError] = useState();
  const [loadError, setLoadError] = useState();
  const unlayerEditor = useRef(null);
  const [customFields, setCustomFields] = useState();
  const [surveys, setSurveys] = useState([]);
  const [dynamicFieldsOpen, setDynamicFieldsOpen] = useState(false);
  const [unlayerLoaded, setUnlayerLoaded] = useState(false);

  const setTab = (bannerTab) => {
    navigate(`${location.pathname}?${queryString.stringify({ ...search, bannerTab })}`);
  };

  const create = (banner) => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/banners`, {
      method: 'POST',
      body: JSON.stringify(banner),
    })
      .then(({ banner }) => onSuccess(banner))
      .catch(setUserError);
  };

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/segments?scope=internal`)
      .then(({ segments }) => {
        setSegments(segments);
      })
      .catch(setLoadError);
  }, [fetch, accountId, domainId]);

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/assets`)
      .then(({ assets }) => {
        setAssets(assets.map((a) => a.url));
      })
      .catch(setLoadError);
  }, [fetch, accountId, domainId]);

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/customFields`)
      .then(({ customFields }) => setCustomFields(customFields))
      .catch(setLoadError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/surveys`)
      .then(({ surveys }) => setSurveys(surveys))
      .catch(setLoadError);
  }, [fetch, accountId, domainId]);

  const processExportedHtml = (chunks, design) => {
    const bgImage = design?.body?.values?.backgroundImage;
    let bgStyle = '';
    if (bgImage?.url) {
      const size = bgImage.size === 'custom' && bgImage.customSize
        ? bgImage.customSize.join(' ')
        : (bgImage.size || 'cover');
      const position = bgImage.position === 'custom' && bgImage.customPosition
        ? bgImage.customPosition.join(' ')
        : (bgImage.position || 'center').replace('-', ' ');
      bgStyle = `background-image: url('${bgImage.url}'); background-size: ${size}; background-repeat: ${bgImage.repeat || 'no-repeat'}; background-position: ${position};`;
    }

    return chunks.body
      .replace(/min-height: 100vh; /g, '')
      .replace(
        '<div id="u_body" class="u_body" style="min-height: 100vh;">',
        `<style>.u-row{display:flex;flex-wrap:nowrap;margin-left:0;margin-right:0}.u-row .u-col{position:relative;width:100%;padding-right:0;padding-left:0}.u-row .u-col.u-col-16p67{flex:0 0 16.67%;max-width:16.67%}.u-row .u-col.u-col-25{flex:0 0 25%;max-width:25%}.u-row .u-col.u-col-33p33{flex:0 0 33.33%;max-width:33.33%}.u-row .u-col.u-col-50{flex:0 0 50%;max-width:50%}.u-row .u-col.u-col-100{flex:0 0 100%;max-width:100%}@media (max-width:767px){.u-row:not(.no-stack){flex-wrap:wrap}.u-row:not(.no-stack) .u-col{flex:0 0 100%!important;max-width:100%!important}}.layout-mobile .u-row:not(.no-stack){flex-wrap:wrap}.layout-mobile .u-row:not(.no-stack) .u-col{flex:0 0 100%!important;max-width:100%!important}@media (max-width:480px){.hide-mobile{display:none!important}.container{max-width:100%!important}}@media (min-width:481px) and (max-width:768px){.hide-tablet{display:none!important}}@media (min-width:481px){.hide-desktop{display:none!important}}</style><div id="u_body" class="u_body" style="width: 100%; ${bgStyle}">`
      );
  };

  const withDesignExported = (cb) => {
    unlayerEditor.current.exportHtml(({ design, chunks }) => {
      cb({
        ...banner,
        design,
        html: processExportedHtml(chunks, design),
      });
    });
  };

  const bannerStringified = JSON.stringify(banner);
  useEffect(() => {
    const currentBanner = JSON.parse(bannerStringified);
    const bannerId = currentBanner.id;
    const bannerEditor = currentBanner.editor;
    const bannerHtml = currentBanner.html;

    if (!bannerId || bannerEditor !== 'visual' || bannerHtml) {
      return;
    }

    const interval = setInterval(() => {
      if (!unlayerEditor.current) {
        return;
      }

      unlayerEditor.current.exportHtml(({ design, chunks }) => {
        if (!chunks?.body) {
          return;
        }

        const bannerDataWithHtml = {
          ...JSON.parse(bannerStringified),
          design,
          html: processExportedHtml(chunks, design),
        };

        clearInterval(interval);

        fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/banners/${bannerId}`, {
          method: 'PUT',
          body: JSON.stringify(bannerDataWithHtml),
        });
      });
    }, 500);

    return () => {
      clearInterval(interval);
    };
  }, [accountId, domainId, bannerStringified, fetch]);

  if (assets === undefined || segments === undefined || customFields === undefined) {
    return '';
  }

  const switchToHtml = (cb) => {
    withDesignExported((modifiedBanner) => {
      setBanner({ ...modifiedBanner, editor: 'html' });
      cb();
    });
  };

  const handleStateChange = (e) => {
    const { name, value } = e.target;
    let change;

    // A chrome field names the `cssStyles` key it edits, not a column on the banner.
    if (CHROME_CSS_STYLE_KEYS.has(name)) {
      change = {
        ...banner,
        cssStyles: { ...banner.cssStyles, [name]: value },
      };
    } else if (name === 'urlFilter' && value === false) {
      change = {
        ...banner,
        urlRegex: '',
        urlRegexNoMatch: '',
      };
    } else {
      change = {
        ...banner,
        [name]: value,
      };
    }

    if (name === 'displayType' && value === 'static') {
      change.trigger = 'immediately';
    }

    setBanner(change); // Setting the new state after all changes
  };

  const validateBanner = (banner) => {
    if (!banner.name) {
      return false;
    }

    if (!banner.segmentId) {
      return false;
    }

    if (banner.displayType === 'popup' && !banner.trigger) {
      return false;
    }

    if (banner.displayType === 'static' && !banner.cssSelector) {
      return false;
    }

    if (banner.editor === 'visual' && !unlayerLoaded) {
      return false;
    }

    return true;
  };

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  const adjustBannerProperties = (banner, trigger) => {
    return {
      ...banner,
      delaySeconds: trigger === 'delaySeconds' ? banner.delaySeconds : null,
      scrollPercentage: trigger === 'scrollPercentage' ? banner.scrollPercentage : null,
    };
  };

  const doSaveBanner = () => {
    const shouldAdjustProperties = banner.displayType === 'popup' && banner.trigger;
    const baseBanner = shouldAdjustProperties ? adjustBannerProperties(banner, banner.trigger) : banner;

    const saveOrUpdateBanner = (bannerData) => {
      saveBanner ? saveBanner(bannerData) : create(bannerData);
    };

    if (banner.editor === 'visual') {
      withDesignExported((modifiedBanner) => {
        const adjustedBanner = shouldAdjustProperties
          ? adjustBannerProperties(modifiedBanner, banner.trigger)
          : modifiedBanner;
        saveOrUpdateBanner(adjustedBanner);
      });
    } else {
      saveOrUpdateBanner(baseBanner);
    }
  };

  const mergeTags = getMergeTags(customFields, [], surveys).filter(
    (el) =>
      ![
        'profileId',
        'unsubscribeLink',
        'subscribeLink',
        'viewInBrowserLink',
        'subscriptionManagementLink',
        'dynamicCoupons',
      ].includes(el.key)
  );

  if (banner === undefined) {
    return '';
  }

  let deleteButton;
  if (typeof deleteBanner === 'function') {
    deleteButton = (
      <LockableButton restrictionKey="bannersDelete" color="red" onClick={() => setConfirmDeleteOpen(true)}>
        <IconTrash size={14} /> Delete
      </LockableButton>
    );
  }

  let copyBannerButton;
  if (typeof copyBanner === 'function') {
    copyBannerButton = (
      <LockableButton restrictionKey="bannersCreate" color="cyan" onClick={copyBanner}>
        <IconCopy size={14} /> Make a copy
      </LockableButton>
    );
  }

  const bannerTypeOptions = [
    { label: 'Show always', value: 'showAlways' },
    { label: 'Show until click', value: 'showUntilClick' },
  ];

  const saveButton = (
    <LockableButton
      restrictionKey={`banners${banner.id ? 'Update' : 'Create'}`}
      disabled={!validateBanner(banner)}
      variant="primary"
      onClick={() => doSaveBanner()}
      data-tour="bannerSaveBtn"
    >
      <IconDeviceFloppy size={14} /> Save
    </LockableButton>
  );

  const nextButton = (
    <Button
      onClick={() => {
        setTab(tabs[tabs.indexOf(bannerTab) + 1]);
        window.scrollTo(0, 0);
      }}
    >
      Next <IconChevronRight size={14} />
    </Button>
  );

  return (
    <>
      <Tabs value={bannerTab} onChange={setTab} mb="md" keepMounted>
        <Tabs.List>
          <Tabs.Tab value="content">Content</Tabs.Tab>
          <Tabs.Tab value="audience" data-tour="bannerAudienceTab">Audience</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="content" pt={0}>
          <ContentBannerForm
            banner={banner}
            handleStateChange={handleStateChange}
            dynamicFieldsOpen={dynamicFieldsOpen}
            mergeTags={mergeTags}
            setDynamicFieldsOpen={setDynamicFieldsOpen}
            setUserError={setUserError}
            unlayerEditor={unlayerEditor}
            accountId={accountId}
            domainId={domainId}
            setLoadError={setLoadError}
            switchToHtml={switchToHtml}
            unlayerLoaded={unlayerLoaded}
            setUnlayerLoaded={setUnlayerLoaded}
          />
        </Tabs.Panel>
        <Tabs.Panel value="audience" pt={0}>
          <AudienceBannerForm
            banner={banner}
            handleStateChange={handleStateChange}
            bannerTypeOptions={bannerTypeOptions}
          />
        </Tabs.Panel>
      </Tabs>
      <Group justify="flex-end" gap="xs" py="md">
        {banner.id || tabs.indexOf(bannerTab) === 0 ? (
          <Button variant="default" onClick={() => cancel()}>
            Cancel
          </Button>
        ) : (
          <Button variant="default" onClick={() => setTab(tabs[tabs.indexOf(bannerTab) - 1])}>
            Back
          </Button>
        )}
        {deleteButton}
        {copyBannerButton}
        {banner.id || tabs.indexOf(bannerTab) === tabs.length - 1 ? saveButton : nextButton}
      </Group>
      <ConfirmationModal
        show={confirmDeleteOpen}
        close={() => setConfirmDeleteOpen(false)}
        confirm={(cb) => deleteBanner(cb)}
        message="This action cannot be undone!"
      />

      {userError && (
        <div style={{ textAlign: 'right' }}>
          <ErrorMessage message={userError.message} />
        </div>
      )}
    </>
  );
};

export default BannerForm;
